import { ChevronLeftIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Container,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Heading,
    Input,
    Link,
    Text,
    useToast,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { FC } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { Controller, useForm } from 'react-hook-form';
import {
    LoaderFunctionArgs,
    Link as ReactRouterLink,
    useLoaderData,
    useNavigate,
} from 'react-router-dom';
import { fetchStock } from '../../api/repository/stock';
import { PagePath } from '../../constants/routes';
import { convertStockItemFromApi, findStock } from '../../helpers/stock';
import { useReturnStock } from '../../hooks/useReturnStock';
import { StockItemInterface } from '../../types/stock';
import { DateInput } from '../DateInput';
import {convertAppointmentFromApi} from "../../helpers/appointment";
import {fetchAppointments} from "../../api/repository/appointment";

export const loader = async ({ params }: LoaderFunctionArgs) => {
    const { id: stockNumber } = params;
    const stock = await fetchStock();
    const appointments = await fetchAppointments();

    const formattedAppointments = appointments.map((item) =>
        convertAppointmentFromApi(item)
    );

    const formattedStock = stock?.map((item) => convertStockItemFromApi(item, formattedAppointments));

    const currentStock = stockNumber
        ? findStock(formattedStock, stockNumber)
        : null;

    if (!currentStock) {
        throw new Response('Not Found', { status: 404 });
    }

    return { currentStock };
};

export const StockReturn: FC = () => {
    const { currentStock } = useLoaderData() as {
        currentStock: StockItemInterface;
    };
    const navigate = useNavigate();
    const { mutateAsync, isPending } = useReturnStock();
    const toast = useToast();

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm({
        defaultValues: {
            stockNumber: currentStock.stockNumber,
            desiredDeliveryDate: '',
            remarks: '',
        },
    });

    const onSubmit = async (values: {
        remarks: string;
        stockNumber: string;
        desiredDeliveryDate: string;
    }) => {
        try {
            await mutateAsync({
                stockNumber: currentStock.stockNumber,
                stock: {
                    remarks: !!values.remarks ? values.remarks : null,
                    desiredDeliveryDate: values.desiredDeliveryDate,
                },
            });

            navigate(PagePath.StockOverview);

            toast({
                title: 'Gelukt! We hebben je afspraak in goede orde ontvangen.',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Er ging iets mis met het versturen van je afspraak.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        }
    };

    return (
        <Box px={8} py={8}>
            <Link
                as={ReactRouterLink}
                to={PagePath.StockOverview}
                mb={2}
                display={'inline-block'}
            >
                <ChevronLeftIcon />
                Terug naar overzicht
            </Link>
            <Container px={0}>
                <Heading color={'secondary'}>Voertuig inleveren</Heading>
                <Text mb={6}>
                    Maak eenvoudig online een afspraak voor het inleveren van
                    een van uw voertuigen.
                </Text>

                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <FormControl mb={4} isDisabled={isPending}>
                        <FormLabel>Voertuig</FormLabel>
                        <Input {...register('stockNumber')} isDisabled={true} />
                    </FormControl>

                    <FormControl
                        mb={4}
                        isDisabled={isPending}
                        isInvalid={!!errors.desiredDeliveryDate}
                    >
                        <FormLabel>Gewenste aanleverdatum</FormLabel>
                        <Controller
                            control={control}
                            name={'desiredDeliveryDate'}
                            rules={{
                                required: 'Verplicht veld',
                            }}
                            render={({
                                field: { onChange, value, disabled },
                            }) => (
                                <DateInput
                                    onChange={(val) => {
                                        const dateString = val
                                            ? format(val, 'yyyy-MM-dd')
                                            : '';
                                        onChange(dateString);
                                    }}
                                    value={value ? new Date(value) : null}
                                    disabled={disabled}
                                />
                            )}
                        />
                        <FormErrorMessage>
                            {errors.desiredDeliveryDate?.message}
                        </FormErrorMessage>
                    </FormControl>

                    <FormControl mb={4} isDisabled={isPending}>
                        <FormLabel>Opmerkingen</FormLabel>
                        <Input
                            {...register('remarks')}
                            placeholder={'Vul je opmerking in'}
                        />
                    </FormControl>

                    <Button
                        type={'submit'}
                        colorScheme={'primary'}
                        isLoading={isPending}
                    >
                        Verzend aanvraag
                    </Button>
                </form>
            </Container>
        </Box>
    );
};
